import React, { useEffect, useState } from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { RefreshCw } from "lucide-react";
import { adminApi } from "@/lib/api";

const TOPIC_COLORS = [
    "#7E2A6C", "#A55691", "#536F55", "#3E5640", "#D4AF37",
    "#B7912C", "#C026D3", "#6B2058", "#94A892", "#8C7022",
    "#536F55", "#581849",
];

export default function Topics() {
    const [data, setData] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = () => adminApi.get("/api/admin/analytics/topics").then((r) => setData(r.data));

    useEffect(() => { load(); }, []);

    const refresh = async () => {
        setRefreshing(true);
        try {
            await adminApi.post("/api/admin/analytics/refresh", null, { params: { limit: 100 } });
            await load();
        } finally { setRefreshing(false); }
    };

    if (!data) return <div className="p-10 text-stone-500">Loading…</div>;

    const topicKeys = data.topics || [];

    return (
        <div className="p-10 max-w-7xl">
            <div className="mb-8 flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">Analytics</div>
                    <h1 className="font-heading text-5xl text-orchid-700">Topics</h1>
                    <p className="text-stone-600 mt-2">
                        What guests are asking about, AI-classified across {data.total} conversations.
                    </p>
                </div>
                <button
                    onClick={refresh}
                    disabled={refreshing}
                    data-testid="topics-refresh"
                    className="flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 disabled:opacity-60"
                >
                    <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
                    {refreshing ? "Classifying…" : "Refresh"}
                </button>
            </div>

            {data.total === 0 ? (
                <div className="bg-white border border-stone-200 rounded-lg p-12 text-center text-stone-500">
                    No classified conversations yet. Click <strong>Refresh</strong> to analyse recent chats.
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                        {/* Bar chart of topics */}
                        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-lg p-6">
                            <h3 className="font-heading text-2xl text-orchid-700 mb-5">Topic Volume</h3>
                            <div className="h-72 min-h-72" data-testid="chart-topic-bar">
                                <ResponsiveContainer width="100%" height={288}>
                                    <BarChart data={data.by_topic} layout="vertical" margin={{ left: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" horizontal={false} />
                                        <XAxis type="number" tick={{ fill: "#78716c", fontSize: 11 }} allowDecimals={false} />
                                        <YAxis dataKey="topic" type="category" tick={{ fill: "#78716c", fontSize: 11 }} width={140} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#7E2A6C" radius={[0, 6, 6, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Pie */}
                        <div className="bg-white border border-stone-200 rounded-lg p-6">
                            <h3 className="font-heading text-2xl text-orchid-700 mb-5">Distribution</h3>
                            <div className="h-72 min-h-72" data-testid="chart-topic-pie">
                                <ResponsiveContainer width="100%" height={288}>
                                    <PieChart>
                                        <Pie data={data.by_topic} dataKey="count" nameKey="topic" outerRadius={90} innerRadius={50}>
                                            {data.by_topic.map((entry, i) => (
                                                <Cell key={entry.topic} fill={TOPIC_COLORS[i % TOPIC_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Over time */}
                    {data.series?.length > 0 && (
                        <div className="bg-white border border-stone-200 rounded-lg p-6 mb-8">
                            <h3 className="font-heading text-2xl text-orchid-700 mb-5">Topics over time</h3>
                            <div className="h-64 min-h-64" data-testid="chart-topic-series">
                                <ResponsiveContainer width="100%" height={256}>
                                    <AreaChart data={data.series}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fill: "#78716c", fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                                        <YAxis tick={{ fill: "#78716c", fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        {topicKeys.slice(0, 6).map((t, i) => (
                                            <Area key={t} type="monotone" dataKey={t} stackId="1"
                                                stroke={TOPIC_COLORS[i]} fill={TOPIC_COLORS[i]} fillOpacity={0.5} />
                                        ))}
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* Detail table */}
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm" data-testid="topics-table">
                            <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-[0.1em]">
                                <tr><th className="text-left px-5 py-3">Topic</th><th className="text-left px-5 py-3">Conversations</th><th className="text-left px-5 py-3">Share</th></tr>
                            </thead>
                            <tbody>
                                {data.by_topic.map((t, i) => (
                                    <tr key={t.topic} className="border-t border-stone-100">
                                        <td className="px-5 py-3 flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: TOPIC_COLORS[i % TOPIC_COLORS.length] }} />
                                            {t.topic}
                                        </td>
                                        <td className="px-5 py-3 text-stone-700">{t.count}</td>
                                        <td className="px-5 py-3 text-stone-700">{t.pct}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
